/*
 * Copyright (c) 2008-2022 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */

/* global log */

import Vue from "vue"
import api from "@/config/api"
import { LOAD_STATES } from "../utils/PagedRequestHandler"
import { EventBus } from "~/common/src/event-bus"
import {
  MEETING_LOADED,
  MEETINGS_LOADED,
  MEETING_CREATED,
  MEETINGS_CREATED,
  MEETING_PERMISSIONS_LOADED,
} from "../config/events"
import store from "./index"

EventBus.$on([MEETING_LOADED, MEETINGS_LOADED, MEETING_CREATED, MEETINGS_CREATED], () => {
  store.dispatch("auth/loadMeetingPermissions")
})

/**
 * Store module keeping track of information related to authentication and authorization
 * (e.g. access token, authentication status, permissions)
 */
export default {
  namespaced: true,

  state: {
    status: null,
    accessToken: null,
    permissions: {
      status: LOAD_STATES.UNLOADED,
      list: [],
    },
    meetingPermissions: {
      status: LOAD_STATES.UNLOADED,
      list: {},
    },
  },

  mutations: {
    /**
     * commit redirecting status to state
     */
    authRedirecting(state) {
      state.status = "redirecting"
    },

    /**
     * commit authentication success to state
     *
     * @param state
     * @param accessToken
     */
    authSuccess(state, accessToken) {
      Vue.http.headers.common["Authorization"] = accessToken.token_type + " " + accessToken.value
      state.status = "success"
      state.accessToken = accessToken
    },

    /**
     * commit authentication error to state
     */
    authError(state) {
      state.status = "error"
    },

    /**
     * commit logout to state
     */
    authLogout: (state) => {
      state.accessToken = null
      state.status = "loggedOut"
      Vue.http.headers.common["Authorization"] = null
    },

    /**
     * commit permission's status
     */
    permissionStatus: (state, status) => {
      state.permissions.status = status
    },

    /**
     * commit meeting permission's status
     *
     * @param state
     * @param status
     */
    meetingPermissionStatus: (state, status) => {
      state.meetingPermissions.status = status
    },

    /**
     * commit permissions to state
     */
    loadPermissions: (state, permissions) => {
      state.permissions.list = permissions
    },

    /**
     * commit meeting permissions to state
     *
     * @param state
     * @param permissions
     */
    loadMeetingPermissions: (state, permissions) => {
      permissions.forEach((p) => {
        const mainMeeting = store.getters["meetings/getMainMeetingById"](p["meetingId"])
        Vue.set(state.meetingPermissions.list, mainMeeting.id, p.permissions)
      })
      EventBus.$emit(MEETING_PERMISSIONS_LOADED)
    },
  },

  actions: {
    /**
     * load the currently logged in user's permission
     *
     * @param commit
     */
    loadPermissions({ commit }) {
      commit("permissionStatus", LOAD_STATES.LOADING)
      return new Promise((resolve, reject) => {
        Vue.http.get(api.PERMISSIONS()).then(
          (response) => {
            commit("loadPermissions", response.body)
            commit("permissionStatus", LOAD_STATES.LOADED)
            resolve(response.body)
          },
          () => {
            log.error("Error loading user permissions from server.")
            reject()
          }
        )
      })
    },

    /**
     * load the currently logged in user's meeting permissions
     *
     * @param commit
     * @param rootGetters
     */
    loadMeetingPermissions({ commit, rootGetters }) {
      commit("meetingPermissionStatus", LOAD_STATES.LOADING)
      return new Promise((resolve, reject) => {
        Vue.http.get(api.MEETING_PERMISSIONS(rootGetters["meetings/getIds"])).then(
          (response) => {
            commit("loadMeetingPermissions", response.body)
            commit("meetingPermissionStatus", LOAD_STATES.LOADED)
            resolve(response.body)
          },
          () => {
            log.error("Error loading meeting permissions from server.")
            reject()
          }
        )
      })
    },

    /**
     * load the meeting permissions for a specific meeting id
     *
     * @param commit
     * @param meetingId
     */
    loadMeetingPermissionsById({ commit }, meetingId) {
      commit("meetingPermissionStatus", LOAD_STATES.LOADING)
      return new Promise((resolve, reject) => {
        Vue.http.get(api.MEETING_PERMISSIONS(meetingId)).then(
          (response) => {
            commit("loadMeetingPermissions", response.body)
            commit("meetingPermissionStatus", LOAD_STATES.LOADED)
            resolve(response.body)
          },
          () => {
            log.error("Error loading meeting permissions from server.")
            reject()
          }
        )
      })
    },
  },

  getters: {
    /**
     * get whether app is currently redirecting to authorization server
     *
     * @param state
     * @returns {boolean}
     */
    isRedirecting: (state) => state.status === "redirecting",

    /**
     * get whether user is authenticated
     *
     * @param state
     * @returns {boolean}
     */
    isAuthenticated: (state) => state.status === "success",

    /**
     * return the currently used access token
     *
     * @param state
     * @return {default.getters.accessToken|(function(*))|*|null|string}
     */
    accessToken: (state) => state.accessToken,

    /**
     * return whether the permissions have loaded
     *
     * @param state
     * @returns {boolean}
     */
    permissionsLoaded: (state) => state.permissions.status === LOAD_STATES.LOADED,

    /**
     * determine whether the current user has a specific permission
     *
     * @param state
     * @returns {function(*=): boolean}
     */
    hasPermission: (state) => (permission) => state.permissions.list.includes(permission),

    /**
     * determine whether the current user has all of the specified permissions
     *
     * @param state
     * @returns {function(*): boolean}
     */
    hasPermissions: (state) => (permissions) =>
      permissions.every((permission) => state.permissions.list.includes(permission)),

    /**
     * determines whether the current user has at least one of the specified permissions
     *
     * @param state
     * @returns {boolean}
     */
    hasOneOfPermissions: (state) => (permissions) => {
      for (let key in permissions) {
        // noinspection JSUnfilteredForInLoop - see https://stackoverflow.com/questions/25723674/javascript-possible-iteration-over-unexpected
        if (state.permissions.list.includes(permissions[key])) {
          return true
        }
      }
      return false
    },

    /**
     * return whether the meeting permissions have loaded
     *
     * @param state
     * @returns {boolean}
     */
    meetingPermissionsLoaded: (state) => state.meetingPermissions.status === LOAD_STATES.LOADED,

    /**
     * get the user's permissions for the specified meeting
     *
     * @param state
     * @param meetingId
     * @returns {Array}
     */
    getMeetingPermissions: (state) => (meetingId) => {
      const mainMeeting = store.getters["meetings/getMainMeetingById"](meetingId)
      return state.meetingPermissions.list[mainMeeting.id] !== undefined
        ? state.meetingPermissions.list[mainMeeting.id]
        : []
    },

    /**
     * determines whether the current user has a specific permission on a certain meeting
     *
     * @param state
     * @param meetingId
     * @param permission
     * @returns {boolean}
     */
    hasMeetingPermission: (state) => (meetingId, permission) => {
      const mainMeeting = store.getters["meetings/getMainMeetingById"](meetingId)
      return state.meetingPermissions.list[mainMeeting.id]
        ? state.meetingPermissions.list[mainMeeting.id].includes(permission)
        : false
    },

    /**
     * determines whether the current user has at least one of the specified meeting permissions
     *
     * @param state
     * @param meetingId
     * @param permissions
     * @returns {boolean}
     */
    hasOneOfMeetingPermissions: (state) => (meetingId, permissions) => {
      const mainMeeting = store.getters["meetings/getMainMeetingById"](meetingId)
      if (!state.meetingPermissions.list[mainMeeting.id]) return false

      for (let key in permissions) {
        if (state.meetingPermissions.list[mainMeeting.id].includes(permissions[key])) {
          return true
        }
      }
      return false
    },
  },
}
